import { GenericTable } from "@shared/components/GenericTable";
import { useCurrency } from "@shared/selectors/globalSelectors";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shared/ui/select";
import { PAGE_SIZE } from "@shared/utils/constants";
import { Download } from "lucide-react";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useCycleWiseRun } from "@features/bx/bill-cycle/hooks";
interface BillCycleRunsTableProps {
  billCycleId: string;
}

export function BillCycleRunsTable() {
  const params = useParams();
  const billCycleId = params.id;
  const action = params.action;
  const [searchTerm, setSearchTerm] = useState("");
  const [timePeriod, setTimePeriod] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);

  const { data: runList } = useCycleWiseRun(
    billCycleId,
    currentPage,
    searchTerm.length > 2 ? searchTerm : undefined
    // timePeriod
  );

  const currency = useCurrency();

  const navigate = useNavigate();

  const handleViewReport = (run: any) => {
    navigate(`/bx/bill-setup/billcycle/summary/${run.id}`);
  };

  const handleSearch = (searchValue: string) => {
    setSearchTerm(searchValue);
    setCurrentPage(1);
  };

  const columns = [
    {
      key: "startDate",
      header: "Run Date",
      sortable: true,
      render: (value: string) => (
        <span className="text-sm font-medium">{value}</span>
      ),
    },
    {
      key: "runTime",
      header: "Run Time",
      render: (value: string) => (
        <span className="text-sm font-medium">{value}</span>
      ),
    },
    {
      key: "lastModifiedUserRemoteName",
      header: "Run By",

    },
    {
      key: "consumerCount",
      header: "Consumers",
    },
    {
      key: "totalBills",
      header: "Total Bills",
      render: (_, row) => (
        <span className="text-sm font-medium">{row.actualVsEstimated?.totalBills}</span>
      ),
    },
    {
      key: "actualVsEstimated",
      header: "Actual vs Estimate",
      render: (_, row) => (
          <span className="font-medium">{row.actualVsEstimated?.actualBillsCount} / {row.actualVsEstimated?.estimatedBillsCount}</span>
      ),
    },
    {
      key: "consumption",
      header: "Consumption",
    },
    {
      key: "avgConsumption",
      header: "Average Consumption",
    },
    {
      key: "billedAmount",
      header: "Billed Amount",
      sortable: true,
      render: (value: string) => (
        <div>
          {currency} {value}
        </div>
      ),
    },
    {
      key: "avgBillAmount",
      header: "Avg. Billed Amount",
      sortable: true,
      render: (value: string) => (
        <div>
          {currency} {value}
        </div>
      ),
    },
  ];

  const tableActions = [
    {
      label: "View Details",
      variant: "outline" as const,
      onClick: (row: any, index: number) => {
        handleViewReport(row);
      },
    },
  ];

  // Bulk actions for selected rows
  const bulkActions = [
    {
      label: "Export Selected",
      variant: "outline" as const,
      icon: <Download size={16} />,
      onClick: (selectedData: any[], selectedRowIds: string[]) => {
        console.log(
          "Bulk export:",
          selectedData.map((r) => r.id)
        );
        setSelectedRows([]);
      },
    },
  ];

  // Time period filter options
  const timePeriodOptions = [
    { value: "all", label: "All Time" },
    { value: "this_month", label: "This Month" },
    { value: "last_month", label: "Last Month" },
    { value: "last_3_months", label: "Last 3 Months" },
    { value: "last_6_months", label: "Last 6 Months" },
  ];

  // Custom filters component
  const CustomFilters = () => (
    <div className="flex gap-2">
      <Select
        value={timePeriod}
        onValueChange={(value) => {
          setTimePeriod(value);
          setCurrentPage(1); // Reset to first page when filtering
        }}
      >
        <SelectTrigger className="w-[180px]">
          <SelectValue placeholder="Time Period" />
        </SelectTrigger>
        <SelectContent>
          {timePeriodOptions.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );

  const isSelectable = params.action == "edit";

  return (
    <div className="">
        <h3 className="page-subheading py-4">Run History</h3>
      <GenericTable
        data={runList?.result ?? []}
        columns={columns}
        rowKey="id"
        actions={tableActions}
        actionsType="buttons"
        emptyMessage="No run history available for this bill cycle"
        selectable={isSelectable}
        selectedRows={selectedRows}
        onSelectionChange={setSelectedRows}
        bulkActions={bulkActions}
        filters={<CustomFilters />}
        onSearch={handleSearch}
        searchPlaceholder="Search by initiator..."
        pagination={{
          currentPage,
          totalPages: Math.ceil(runList?.count / PAGE_SIZE),
          totalItems: runList?.count,
          itemsPerPage: PAGE_SIZE,
          startItem: (currentPage - 1) * PAGE_SIZE + 1,
          endItem: Math.min(currentPage * PAGE_SIZE, runList?.count),
        }}
        onPageChange={setCurrentPage}
      />
    </div>
  );
}
